import { useParams } from "wouter";
import {
  lazy,
  Suspense,
  useCallback,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { skipToken } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { formaters } from "vincent-utils";
import type { ExistingPatient } from "cadendar-shared";
import { Button, buttonVariants } from "../../../components/ui/button";
import { trpc } from "../../main/components/trpc";
import useUser from "../../users/hooks/useUser";
import useOwner from "../../users/hooks/useOwner";
import { usePatientPageMetadata } from "../patientPageMetadata";
import PatientOverview from "../components/PatientOverview";
import Flag from "../components/Flag";
import type { PatientHistoryFilter } from "../components/PatientLogosActesList";

const QuotesContainer = lazy(() => import("../../quotes/QuotesContainer"));
const InsuranceCardDisplay = lazy(() => import("./InsuranceCardDisplay"));
const PatientXRaysViewer = lazy(() => import("./PatientXRaysViewer"));
const PatientMediaSection = lazy(() => import("./PatientMediaSection"));
const PatientLogosActesList = lazy(
  () => import("../components/PatientLogosActesList"),
);
const PatientPecDocumentsPanel = lazy(
  () => import("./PatientPecDocumentsPanel"),
);
const PatientIdentityContactPanel = lazy(
  () => import("../components/PatientIdentityContactPanel"),
);
const PatientConsultationsPanel = lazy(
  () => import("../components/PatientConsultationsPanel"),
);
const PatientRevenueSummary = lazy(
  () => import("../components/PatientRevenueSummary"),
);
const PatientTpePaymentPanel = lazy(() =>
  import("../../tpePayments/components/PatientTpePaymentPanel").then(
    (module) => ({ default: module.PatientTpePaymentPanel }),
  ),
);

type PatientTab =
  | "overview"
  | "history"
  | "imaging"
  | "finance"
  | "documents"
  | "identity";
const tabs: Array<{ id: PatientTab; label: string }> = [
  { id: "overview", label: "Vue d’ensemble" },
  { id: "history", label: "Historique et consultations" },
  { id: "imaging", label: "Imagerie" },
  { id: "finance", label: "Devis et paiements" },
  { id: "documents", label: "Documents et PEC" },
  { id: "identity", label: "Administratif" },
];

interface NavigationState {
  activeTab: PatientTab;
  visitedTabs: PatientTab[];
}

function navigate(state: NavigationState, tab: PatientTab) {
  return {
    activeTab: tab,
    visitedTabs: state.visitedTabs.includes(tab)
      ? state.visitedTabs
      : [...state.visitedTabs, tab],
  };
}

function PatientSatisfactionPanel({ patientId }: { patientId: string }) {
  const utils = trpc.useUtils();
  const query = trpc.question.getQuestionsByPatientId.useQuery(patientId);
  const mutation = trpc.question.createQuestion.useMutation({
    onSuccess: () =>
      utils.question.getQuestionsByPatientId.invalidate(patientId),
  });
  const question = query.data?.[0];
  return (
    <section className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 font-semibold text-slate-900">
        Satisfaction patient
      </h2>
      {query.error ? (
        <p role="alert" className="text-sm text-red-700">
          Questionnaire indisponible.
        </p>
      ) : query.isPending ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : question ? (
        <p className="text-sm text-slate-600">
          {question.answers.note == null
            ? "Aucune réponse au questionnaire."
            : `Note donnée : ${question.answers.note}/5`}
        </p>
      ) : (
        <Button
          type="button"
          variant="outline"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ patientId, version: 1 })}
        >
          {mutation.isPending ? "Création…" : "Créer le questionnaire"}
        </Button>
      )}
      {mutation.error ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          Impossible de créer le questionnaire.
        </p>
      ) : null}
    </section>
  );
}

function PatientFile({
  patient,
  canReadConsultations,
}: {
  patient: ExistingPatient;
  canReadConsultations: boolean;
}) {
  const [navigation, dispatchNavigation] = useReducer(navigate, {
    activeTab: "overview",
    visitedTabs: ["overview"],
  });
  const [historyDate, setHistoryDate] = useState<string | null>(null);
  const [historyFilter, setHistoryFilter] =
    useState<PatientHistoryFilter>("all");
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedPecId, setSelectedPecId] = useState<string | null>(null);
  const [selectedXRayKey, setSelectedXRayKey] = useState<string | null>(null);
  const xRayViewerRef = useRef<HTMLDivElement | null>(null);
  const birthdate = DateTime.fromFormat(patient.birthdate, "dd/MM/yyyy");
  const age = birthdate.isValid
    ? Math.floor(DateTime.now().diff(birthdate, "years").years)
    : null;
  const openHistory = (date?: string) => {
    setHistoryDate(date ?? null);
    setHistoryFilter("all");
    dispatchNavigation("history");
  };
  const openQuote = (quoteId?: string) => {
    setSelectedQuoteId(quoteId ?? null);
    dispatchNavigation("finance");
  };
  const openPec = (documentId?: string) => {
    setSelectedPecId(documentId ?? null);
    dispatchNavigation("documents");
  };
  const scrollToXRayImage = useCallback(() => {
    xRayViewerRef.current
      ?.querySelector<HTMLElement>("[data-patient-xray-image-container]")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  function handleTabKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    let nextIndex = index;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft")
      nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else return;
    const tab = tabs[nextIndex];
    if (!tab) return;
    event.preventDefault();
    dispatchNavigation(tab.id);
    event.currentTarget
      .closest("[role=tablist]")
      ?.querySelectorAll<HTMLButtonElement>("[role=tab]")
      [nextIndex]?.focus();
  }

  function panel(tab: PatientTab, children: ReactNode) {
    if (!navigation.visitedTabs.includes(tab)) return null;
    return (
      <section
        key={tab}
        id={`patient-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`patient-tab-${tab}`}
        hidden={navigation.activeTab !== tab}
        tabIndex={0}
        className="outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
      >
        <Suspense
          fallback={
            <p role="status" className="p-4 text-sm text-slate-500">
              Chargement de la rubrique…
            </p>
          }
        >
          {children}
        </Suspense>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-words text-xl font-semibold tracking-tight">
                {patient.name.toLocaleUpperCase("fr")}{" "}
                {formaters.capitaliseEveryFirstLetter(patient.forename)}
              </h1>
              {patient.patient_flags ? (
                <Flag
                  flag={patient.patient_flags}
                  label={
                    patient.patient_flags === "bl"
                      ? "Ne plus donner de RDV"
                      : undefined
                  }
                />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {age !== null && age >= 0 ? `${age} ans · ` : ""}
              {patient.birthdate}
              {patient.logos_file_number !== null
                ? ` · Dossier LOGOS n° ${patient.logos_file_number}`
                : " · Dossier Cadendar"}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
              {patient.portablee164 ? (
                <a
                  className="hover:underline"
                  href={`tel:${patient.portablee164}`}
                >
                  {formaters.formatTelNumberForDisplay(patient.portablee164)}
                </a>
              ) : (
                <span>Portable non renseigné</span>
              )}
              {patient.email ? (
                <a
                  className="break-all hover:underline"
                  href={`mailto:${patient.email}`}
                >
                  {patient.email}
                </a>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {patient.logos_file_number !== null ? (
              <a
                className={buttonVariants({ variant: "outline", size: "sm" })}
                href={`cadendar://logosfile/open/${patient.logos_file_number}`}
              >
                Ouvrir dans LOGOS
              </a>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatchNavigation("identity")}
            >
              Identité et coordonnées
            </Button>
          </div>
        </div>
      </header>
      <div className="px-4 pb-8 sm:px-6">
        <nav
          role="tablist"
          aria-label="Dossier patient"
          className="mb-5 flex gap-5 overflow-x-auto border-b border-slate-200"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              id={`patient-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={navigation.activeTab === tab.id}
              aria-controls={`patient-panel-${tab.id}`}
              tabIndex={navigation.activeTab === tab.id ? 0 : -1}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              onClick={() => dispatchNavigation(tab.id)}
              className={`shrink-0 border-b-2 py-3 text-sm font-medium ${navigation.activeTab === tab.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        {panel(
          "overview",
          <PatientOverview
            patient={patient}
            canReadConsultations={canReadConsultations}
            onOpenHistory={openHistory}
            onOpenConsultations={() => openHistory()}
            onOpenImaging={() => dispatchNavigation("imaging")}
            onOpenQuote={openQuote}
            onOpenPec={openPec}
          />,
        )}
        {panel(
          "history",
          <div className="space-y-4">
            {canReadConsultations ? (
              <PatientConsultationsPanel patientId={patient.id} />
            ) : null}
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Historique complet</h2>
                {historyDate ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHistoryDate(null)}
                  >
                    Afficher toutes les dates
                  </Button>
                ) : null}
              </div>
              {historyDate ? (
                <p className="mt-2 text-sm text-slate-600">
                  Actes et images du{" "}
                  {DateTime.fromISO(historyDate).toFormat("dd/MM/yyyy")}
                </p>
              ) : null}
              <PatientLogosActesList
                patientId={patient.id}
                patientLogosNumber={patient.logos_file_number}
                selectedDate={historyDate}
                eventFilter={historyFilter}
                onFilterChange={setHistoryFilter}
                onOpenInRadios={(key) => {
                  setSelectedXRayKey(key);
                  dispatchNavigation("imaging");
                }}
              />
            </div>
          </div>,
        )}
        {panel(
          "imaging",
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Radios et photos</h2>
            <div ref={xRayViewerRef}>
              <PatientXRaysViewer
                key={selectedXRayKey ?? "default"}
                patientId={patient.id}
                initialSelectedKey={selectedXRayKey}
                onXRaysReady={scrollToXRayImage}
              />
            </div>
            <PatientMediaSection patientId={patient.id} mediaKind="photo" />
          </div>,
        )}
        {panel(
          "finance",
          <div className="space-y-5">
            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="mb-4 text-lg font-semibold">Devis</h2>
              <QuotesContainer
                patientId={patient.id}
                selectedQuoteId={selectedQuoteId}
                onSelectQuote={setSelectedQuoteId}
              />
            </section>
            <PatientTpePaymentPanel patientId={patient.id} />
            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="mb-2 font-semibold">Bilan financier</h2>
              <PatientRevenueSummary
                patientId={patient.id}
                patientLogosNumber={patient.logos_file_number}
              />
            </section>
          </div>,
        )}
        {panel(
          "documents",
          <div>
            <PatientPecDocumentsPanel
              patientId={patient.id}
              expandedDocumentId={selectedPecId}
              onExpandDocument={setSelectedPecId}
            />
            <PatientMediaSection patientId={patient.id} mediaKind="document" />
          </div>,
        )}
        {panel(
          "identity",
          <div>
            <PatientIdentityContactPanel patient={patient} />
            <InsuranceCardDisplay patientId={patient.id} />
            <PatientSatisfactionPanel patientId={patient.id} />
          </div>,
        )}
      </div>
    </div>
  );
}

export default function PatientMainContainer() {
  const { id } = useParams<{ id: string }>();
  const owner = useOwner();
  const user = useUser();
  const query = trpc.patient.getById.useQuery(owner && id ? id : skipToken);
  const patient = query.data?.owner === owner ? query.data : undefined;
  usePatientPageMetadata({ name: patient?.name, forename: patient?.forename });
  if (!owner)
    return (
      <p className="p-4 text-sm text-slate-600">
        Sélectionnez un praticien pour consulter le dossier.
      </p>
    );
  if (query.isPending)
    return (
      <p role="status" className="p-4 text-sm text-slate-500">
        Chargement du dossier patient…
      </p>
    );
  if (query.error)
    return (
      <p role="alert" className="p-4 text-sm text-red-700">
        Impossible de charger le dossier patient.{" "}
        <button
          type="button"
          className="underline"
          onClick={() => void query.refetch()}
        >
          Réessayer
        </button>
      </p>
    );
  if (!patient)
    return (
      <p className="p-4 text-sm text-slate-600">
        Patient introuvable pour ce praticien.
      </p>
    );
  return (
    <PatientFile
      key={`${owner}/${patient.id}`}
      patient={patient}
      canReadConsultations={user?.tag === "doctor"}
    />
  );
}
